import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Sparkles, Lock, ShieldCheck } from "lucide-react";

const loadRazorpay = () => new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
});

/**
 * AccessGate — wraps a member-facing page.
 * - Existing CRM members and staff/admin pass through immediately.
 * - First-time visitors are shown a Razorpay paywall for a Rs. 99 / 30-day website pass.
 */
export const AccessGate = ({ children, user }) => {
    const [status, setStatus] = useState(null); // { allowed, reason, price_inr, validity_days, pass_expires_at }
    const [checking, setChecking] = useState(true);
    const [paying, setPaying] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/access");
            setStatus(data);
        } catch (err) {
            console.error("access check failed", err);
            setStatus({ allowed: false, reason: "error" });
        } finally {
            setChecking(false);
        }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const pay = async () => {
        setPaying(true);
        try {
            const { data: order } = await api.post("/access/order");
            const ok = await loadRazorpay();
            if (!ok) throw new Error("Razorpay unavailable");
            const rzp = new window.Razorpay({
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                name: "All About Eve Fitness",
                description: order.label,
                order_id: order.order_id,
                theme: { color: "#D4AF37" },
                prefill: { name: order.name || user?.name, email: order.email || user?.email },
                handler: async (resp) => {
                    try {
                        await api.post("/access/verify", {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        toast.success("Access unlocked \u2014 welcome in.");
                        refresh();
                    } catch (err) {
                        console.error("verify failed", err);
                        toast.error(err?.response?.data?.detail || "Verification failed");
                    }
                },
                modal: { ondismiss: () => setPaying(false) },
            });
            rzp.open();
        } catch (err) {
            console.error("access pay failed", err);
            toast.error(err?.response?.data?.detail || "Payment could not be started");
        } finally {
            setPaying(false);
        }
    };

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-obsidian">
                <div className="font-mono-label text-sand">Checking access...</div>
            </div>
        );
    }
    if (status?.allowed) return children;

    return (
        <div className="min-h-screen flex items-center justify-center bg-obsidian px-4 py-10"
             data-testid="access-paywall">
            <div className="max-w-lg w-full rounded-2xl p-8 lg:p-10 relative overflow-hidden"
                 style={{
                     background: "radial-gradient(circle at 0% 0%, rgba(220,20,60,0.22) 0%, transparent 50%), radial-gradient(circle at 100% 100%, rgba(212,175,55,0.22) 0%, transparent 55%), linear-gradient(160deg, rgba(18,16,22,0.98) 0%, rgba(9,8,10,0.98) 100%)",
                     border: "1px solid rgba(212,175,55,0.5)",
                     boxShadow: "0 30px 90px rgba(220,20,60,0.28)",
                 }}>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/40 bg-obsidian/60">
                    <Lock className="w-3 h-3 text-gold" />
                    <span className="font-mono-label !text-[10px]">FIRST-TIME VISITOR</span>
                </div>
                <h1 className="font-display text-3xl lg:text-4xl text-ivory mt-4">
                    Welcome, {user?.name?.split(" ")[0] || "there"}.
                </h1>
                <p className="text-sand mt-3">
                    Your email <span className="text-gold">{user?.email}</span> isn&apos;t on the studio
                    roster yet. Unlock the members-only dashboard, cycle-synced nutrition, and
                    Eve chat with a one-time <b className="text-gold">&#8377;{status?.price_inr || 99}</b>
                    &nbsp;pass, valid <b>{status?.validity_days || 30} days</b>.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-sand">
                    <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-gold mt-0.5" /> Members already on the CRM roster login free forever.</li>
                    <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-gold mt-0.5" /> Renews monthly on-demand \u2014 no auto-charge.</li>
                    <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-gold mt-0.5" /> Cancel anytime \u2014 refund within 24 hours if unused.</li>
                </ul>
                <Button onClick={pay} disabled={paying}
                        className="btn-gold w-full mt-7 !py-4 !text-base tracking-wide"
                        data-testid="access-pay-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {paying ? "Opening checkout..." : `Pay \u20B9${status?.price_inr || 99} to unlock access`}
                </Button>
                <div className="text-[11px] text-sand/60 mt-4 text-center">
                    Secure checkout by Razorpay &middot; INR settlement &middot; No card details stored on our servers.
                </div>
                <div className="text-[11px] text-sand/50 mt-4 text-center">
                    Already a member? Ask reception to add your email to the CRM
                    (<a href="tel:+917032690303" className="text-gold">+91 70326 90303</a>) and refresh this page.
                </div>
            </div>
        </div>
    );
};
